import { useState, useEffect, useCallback } from 'react';
import MetricCard from './components/MetricCard';
import DailyChart from './components/DailyChart';
import MonthlyChart from './components/MonthlyChart';
import PerformanceMap from './components/PerformanceMap';
import RecentCampaigns from './components/RecentCampaigns';
import QuickUsageWidget from './components/QuickUsageWidget';
import AppointmentBadge from './components/AppointmentBadge';
import TrialBanner from './components/TrialBanner';
import CurrentPlanBadge from './components/CurrentPlanBadge';
import BillingSummaryCard from './components/BillingSummaryCard';
import SkeletonMetricCard from './components/SkeletonMetricCard';
import SkeletonChart from './components/SkeletonChart';
import { SkeletonStatusCards, SkeletonTable, SkeletonMap } from './components/SkeletonPanels';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

interface DashboardData {
  totalContacts: number;
  totalCampaigns: number;
  messagesSent: number;
  messagesDelivered: number;
  messagesRead: number;
  messagesReplied: number;
  activeCampaigns: number;
  scheduledCampaigns: number;
  failures: number;
  recentLogs: number;
}

interface CampaignSummary {
  id: string;
  name: string;
  status: string;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  progress: number;
}

interface DailyDataPoint {
  day: string;
  enviadas: number;
  entregues: number;
  lidas: number;
}

interface MonthlyDataPoint {
  month: string;
  enviadas: number;
  entregues: number;
}

interface StateDataPoint {
  state: string;
  delivered: number;
  read: number;
}

const emptyDashboard: DashboardData = {
  totalContacts: 0,
  totalCampaigns: 0,
  messagesSent: 0,
  messagesDelivered: 0,
  messagesRead: 0,
  messagesReplied: 0,
  activeCampaigns: 0,
  scheduledCampaigns: 0,
  failures: 0,
  recentLogs: 0,
};

const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function DashboardPage() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashData, setDashData] = useState<DashboardData>(emptyDashboard);
  const [recentCampaigns, setRecentCampaigns] = useState<CampaignSummary[]>([]);
  const [dailyData, setDailyData] = useState<DailyDataPoint[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyDataPoint[]>([]);
  const [stateData, setStateData] = useState<StateDataPoint[]>([]);

  const fetchDashboard = useCallback(async () => {
    if (!profile?.company_id) {
      setLoading(false);
      return;
    }

    try {
      const cid = profile.company_id;

      // 1. Contacts count
      const { count: totalContacts } = await supabase
        .from('contacts')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', cid);

      // 2. Campaigns data
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id, name, status, sent_count, delivered_count, read_count, replied_count, failed_count, created_at')
        .eq('company_id', cid)
        .order('created_at', { ascending: false });

      const totalCampaigns = campaigns?.length || 0;

      const messagesSent = campaigns?.reduce((sum, c) => sum + (c.sent_count || 0), 0) || 0;
      const messagesDelivered = campaigns?.reduce((sum, c) => sum + (c.delivered_count || 0), 0) || 0;
      const messagesRead = campaigns?.reduce((sum, c) => sum + (c.read_count || 0), 0) || 0;
      const messagesReplied = campaigns?.reduce((sum, c) => sum + (c.replied_count || 0), 0) || 0;
      const activeCampaigns = campaigns?.filter((c) => c.status === 'active').length || 0;
      const scheduledCampaigns = campaigns?.filter((c) => c.status === 'scheduled').length || 0;
      const failures = campaigns?.reduce((sum, c) => sum + (c.failed_count || 0), 0) || 0;

      // 3. Recent logs count
      const { count: recentLogs } = await supabase
        .from('logs')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', cid);

      setDashData({
        totalContacts: totalContacts || 0,
        totalCampaigns,
        messagesSent,
        messagesDelivered,
        messagesRead,
        messagesReplied,
        activeCampaigns,
        scheduledCampaigns,
        failures,
        recentLogs: recentLogs || 0,
      });

      // 4. Recent campaigns (top 5)
      const top5 = (campaigns || []).slice(0, 5).map((c) => ({
        id: c.id,
        name: c.name,
        status: c.status,
        sent: c.sent_count || 0,
        delivered: c.delivered_count || 0,
        read: c.read_count || 0,
        replied: c.replied_count || 0,
        progress: c.sent_count > 0
          ? Math.round(((c.delivered_count || 0) / c.sent_count) * 100)
          : 0,
      }));
      setRecentCampaigns(top5);

      // 5. Daily chart: last 7 days from queue_jobs joined to campaigns
      if (totalCampaigns > 0) {
        try {
          const campaignIds = (campaigns || []).map((c) => c.id);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
          sevenDaysAgo.setHours(0, 0, 0, 0);

          const { data: dailyJobs } = await supabase
            .from('queue_jobs')
            .select('status, processed_at')
            .in('campaign_id', campaignIds)
            .gte('processed_at', sevenDaysAgo.toISOString());

          const dailyMap: Record<string, { enviadas: number; entregues: number; lidas: number }> = {};
          for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const key = dayNames[d.getDay()];
            dailyMap[key] = { enviadas: 0, entregues: 0, lidas: 0 };
          }

          (dailyJobs || []).forEach((job) => {
            if (!job.processed_at) return;
            const d = new Date(job.processed_at);
            const key = dayNames[d.getDay()];
            if (!dailyMap[key]) return;
            dailyMap[key].enviadas += 1;
            if (job.status === 'delivered' || job.status === 'sent') dailyMap[key].entregues += 1;
            if (job.status === 'read') dailyMap[key].lidas += 1;
          });

          const orderedDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (6 - i));
            return dayNames[d.getDay()];
          });

          setDailyData(
            orderedDays.map((day) => ({
              day,
              enviadas: dailyMap[day]?.enviadas || 0,
              entregues: dailyMap[day]?.entregues || 0,
              lidas: dailyMap[day]?.lidas || 0,
            }))
          );
        } catch {
          setDailyData([]);
        }
      } else {
        setDailyData([]);
      }

      // 6. Monthly chart: aggregate campaigns by month
      if (totalCampaigns > 0) {
        const monthlyMap: Record<string, { enviadas: number; entregues: number }> = {};
        const currentYear = new Date().getFullYear();

        for (let m = 0; m < 8; m++) {
          const monthIdx = m;
          const key = monthNames[monthIdx];
          monthlyMap[key] = { enviadas: 0, entregues: 0 };
        }

        (campaigns || []).forEach((c) => {
          if (!c.created_at) return;
          const d = new Date(c.created_at);
          if (d.getFullYear() !== currentYear) return;
          const key = monthNames[d.getMonth()];
          if (!monthlyMap[key]) return;
          monthlyMap[key].enviadas += c.sent_count || 0;
          monthlyMap[key].entregues += c.delivered_count || 0;
        });

        setMonthlyData(
          monthNames.slice(0, 8).map((month) => ({
            month,
            enviadas: monthlyMap[month]?.enviadas || 0,
            entregues: monthlyMap[month]?.entregues || 0,
          }))
        );
      } else {
        setMonthlyData([]);
      }

      // 7. Performance by state: group contacts
      if ((totalContacts || 0) > 0) {
        const { data: contacts } = await supabase
          .from('contacts')
          .select('state')
          .eq('company_id', cid)
          .not('state', 'is', null);

        const stateCounts: Record<string, number> = {};
        (contacts || []).forEach((c) => {
          const s = (c.state || '').toUpperCase().trim();
          if (!s) return;
          stateCounts[s] = (stateCounts[s] || 0) + 1;
        });

        setStateData(
          Object.entries(stateCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 8)
            .map(([state, count]) => ({
              state,
              delivered: count * 3,
              read: count * 2,
            }))
        );
      } else {
        setStateData([]);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Erro ao carregar dados do dashboard.');
    } finally {
      setLoading(false);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const m = dashData;

  const deliveryRate = m.messagesSent > 0 ? ((m.messagesDelivered / m.messagesSent) * 100).toFixed(1) : '0.0';
  const readRate = m.messagesDelivered > 0 ? ((m.messagesRead / m.messagesDelivered) * 100).toFixed(1) : '0.0';
  const replyRate = m.messagesDelivered > 0 ? ((m.messagesReplied / m.messagesDelivered) * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">Dashboard</h2>
        <p className="text-sm text-foreground-500 mt-1">Visão geral do desempenho das suas campanhas</p>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/8 border border-red-200 dark:border-red-500/15 rounded-xl p-4 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
          <button
            onClick={fetchDashboard}
            className="px-3 py-1.5 bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors whitespace-nowrap cursor-pointer"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Appointment Badge */}
      <AppointmentBadge />

      {/* Current Plan + Trial Banner */}
      <CurrentPlanBadge />
      <TrialBanner />

      {/* Billing Summary */}
      <BillingSummaryCard />

      {/* Metrics grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonMetricCard key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard
            label="Contatos"
            value={m.totalContacts}
            icon="ri-contacts-book-line"
            trend={m.totalContacts > 0 ? { value: 0, isPositive: true } : undefined}
            colorClass="text-primary-500"
            bgClass="bg-primary-50 dark:bg-primary-500/10"
          />
          <MetricCard
            label="Campanhas"
            value={m.totalCampaigns}
            icon="ri-megaphone-line"
            trend={m.totalCampaigns > 0 ? { value: 0, isPositive: true } : undefined}
            colorClass="text-accent-500"
            bgClass="bg-accent-50 dark:bg-accent-500/10"
          />
          <MetricCard
            label="Enviadas"
            value={m.messagesSent}
            icon="ri-send-plane-line"
            colorClass="text-secondary-500"
            bgClass="bg-secondary-50 dark:bg-secondary-500/10"
          />
          <MetricCard
            label={`Entregues (${deliveryRate}%)`}
            value={m.messagesDelivered}
            icon="ri-check-double-line"
            colorClass="text-emerald-500"
            bgClass="bg-emerald-50 dark:bg-emerald-500/10"
          />
          <MetricCard
            label={`Lidas (${readRate}%)`}
            value={m.messagesRead}
            icon="ri-eye-line"
            colorClass="text-sky-500"
            bgClass="bg-sky-50 dark:bg-sky-500/10"
          />
          <MetricCard
            label={`Respondidas (${replyRate}%)`}
            value={m.messagesReplied}
            icon="ri-reply-line"
            colorClass="text-amber-500"
            bgClass="bg-amber-50 dark:bg-amber-500/10"
          />
        </div>
      )}

      {/* Status cards + Quick Usage Widget row */}
      {loading ? (
        <SkeletonStatusCards />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 p-4 flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${m.activeCampaigns > 0 ? 'bg-emerald-500 animate-pulse' : 'bg-background-300'}`} />
            <div>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{m.activeCampaigns}</p>
              <p className="text-xs text-foreground-500">Campanhas Ativas</p>
            </div>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 p-4 flex items-center gap-3">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
            <div>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{m.scheduledCampaigns}</p>
              <p className="text-xs text-foreground-500">Agendadas</p>
            </div>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 p-4 flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${m.failures > 0 ? 'bg-red-500' : 'bg-background-300'}`} />
            <div>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{m.failures.toLocaleString('pt-BR')}</p>
              <p className="text-xs text-foreground-500">Falhas</p>
            </div>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 p-4 flex items-center gap-3">
            <i className="ri-file-list-3-line text-foreground-400 text-lg" />
            <div>
              <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{m.recentLogs}</p>
              <p className="text-xs text-foreground-500">Logs Recentes</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Usage Widget + Daily Chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <QuickUsageWidget />
        </div>
        <div className="lg:col-span-2">
          {loading ? <SkeletonChart /> : <DailyChart data={dailyData} />}
        </div>
      </div>

      {/* Monthly chart */}
      {loading ? (
        <SkeletonChart />
      ) : (
        <MonthlyChart data={monthlyData} />
      )}

      {/* Performance map + Recent campaigns */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <SkeletonMap />
          </div>
          <div className="lg:col-span-3">
            <SkeletonTable />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <PerformanceMap data={stateData} />
          </div>
          <div className="lg:col-span-3">
            <RecentCampaigns campaigns={recentCampaigns} />
          </div>
        </div>
      )}
    </div>
  );
}